/**
 * useReportMetrics — calls the server-side aggregator RPC
 * `get_report_metrics` and returns the typed payload.
 *
 * Server-side aggregation (instead of pulling row sets into the
 * client) so the page scales to large orgs and so the client never
 * touches column literals like rooms.current_status that the
 * contract layer protects (CLAUDE.md ERROR 5 / rule 4).
 *
 * Spec: PROJECT_STATE task #78.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useRole }                        from './useAuth'

export type ReportRoomStatus =
  | 'occupied'
  | 'move_out_pending'
  | 'vacant_dirty'
  | 'cleaning_assigned'
  | 'cleaning_in_progress'
  | 'inspection_pending'
  | 'maintenance_required'
  | 'ready'
  | 'reserved'

export type ReportServiceTier =
  | 'touch_up'
  | 'normal'
  | 'heavy'
  | 'partial'
  | 'special'
  | 'exceeds_scope_pending_owner_approval'

export type ReportFeeRequestStatus =
  | 'requested'
  | 'approved'
  | 'denied'
  | 'completed_without_approval'
  | 'paid_at_normal_clean_rate'

export interface InspectionsTierRow {
  service_tier:       ReportServiceTier
  passed:             number
  failed:             number
  re_clean_required:  number
  blocked:            number
  cancelled:          number
  /** Terminal rows (passed + failed + re_clean + blocked); excludes cancelled and non-terminal. */
  terminal_total:     number
  /** passed / terminal_total — null when terminal_total is 0; rounded to 3 decimals server-side. */
  pass_rate:          number | null
}

export interface CountersByCleanerRow {
  cleaner_id:        string
  cleaner_name:      string
  /**
   * Highest tier the assignee currently holds in the task's org per
   * profile_roles (super_admin > contractor_admin > supervisor >
   * cleaner > maintenance > client_view). Null when the user has no
   * grant in that org (rare — only on a user demoted after completing
   * the task). Surfaced 2026-05-18 so the Reports table can label
   * supervisor-as-cleaner work honestly.
   */
  assignee_role:     string | null
  trash_bag_total:   number
  appliances_total:  number
  tasks_total:       number
}

export interface ReportMetrics {
  rooms_by_status:           Partial<Record<ReportRoomStatus, number>>
  partial_unit_approved:     number
  fee_requests_by_status:    Partial<Record<ReportFeeRequestStatus, number>>
  paid_at_normal_rate_count: number
  exceedance_count:          number
  inspections_by_tier:       InspectionsTierRow[]
  /** Operational counter aggregates (#74a billing surface, 2026-05-15). */
  total_trash_bags:          number
  total_appliances_cleaned:  number
  counters_by_cleaner:       CountersByCleanerRow[]
}

export function useReportMetrics(): UseQueryResult<ReportMetrics> {
  const supabase = useSupabase()
  const role     = useRole()

  return useQuery({
    queryKey: ['report-metrics', role],
    staleTime: 60_000,
    enabled:   role === 'super_admin' || role === 'contractor_admin',
    queryFn: async (): Promise<ReportMetrics> => {
      // Role + org_id are derived inside the RPC from auth.jwt() →
      // app_metadata (server-controlled). Passing them as args would
      // let the client claim a role it doesn't have.
      const { data, error } = await supabase.rpc('get_report_metrics')
      if (error) throw error
      return data as ReportMetrics
    },
  })
}
